import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { CommonService } from 'wire-frame';
import { Subscription, timeout } from 'rxjs';
import { ApiService } from '../../../service/admin/api.service';
import { NotificationsService } from '../../../service/admin/notifications.service';

@Component({
  standalone: true,
  selector: 'app-admin-logout',
  template: `
    <div class="logout-screen" role="status" aria-live="polite">
      <div class="logout-card">
        <div class="logout-mark" aria-hidden="true">
          <i class="pi pi-sign-out"></i>
        </div>
        <p class="logout-eyebrow">Dr. Subodh Bhade Clinic</p>
        <h1>Signing you out</h1>
        <p class="logout-copy">Clearing your session and returning to login.</p>
        <div class="logout-spinner" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .logout-screen {
        position: fixed;
        inset: 0;
        z-index: 10000;
        display: grid;
        place-items: center;
        padding: 1.5rem;
        background:
          radial-gradient(900px 360px at 8% -10%, rgba(15, 118, 110, 0.14), transparent 55%),
          radial-gradient(700px 280px at 100% 100%, rgba(37, 99, 235, 0.08), transparent 50%),
          linear-gradient(180deg, #f7faf8 0%, #eef3f0 100%);
        color: #102a43;
        font-family: var(--app-font-family, 'Inter', system-ui, sans-serif);
      }

      .logout-card {
        width: min(22rem, 100%);
        padding: 1.75rem 1.5rem 1.55rem;
        border: 1px solid rgba(16, 42, 67, 0.1);
        border-radius: 18px;
        background: rgba(255, 253, 249, 0.96);
        box-shadow: 0 18px 40px rgba(16, 42, 67, 0.1);
        text-align: center;
        backdrop-filter: blur(8px);
      }

      .logout-mark {
        display: inline-grid;
        place-items: center;
        width: 3rem;
        height: 3rem;
        margin: 0 auto 0.9rem;
        border-radius: 14px;
        color: #f8fffc;
        background: linear-gradient(145deg, #128277 0%, #0b5f59 100%);
        box-shadow: 0 10px 22px rgba(15, 118, 110, 0.28);
      }

      .logout-mark .pi {
        font-size: 1.05rem;
      }

      .logout-eyebrow {
        margin: 0 0 0.35rem;
        font-size: 0.7rem;
        font-weight: 750;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: #0f766e;
      }

      h1 {
        margin: 0 0 0.4rem;
        font-size: 1.35rem;
        font-weight: 800;
        letter-spacing: -0.03em;
        color: #0f172a;
      }

      .logout-copy {
        margin: 0 0 1.15rem;
        color: #627d98;
        font-size: 0.9rem;
        line-height: 1.45;
      }

      .logout-spinner {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.35rem;
      }

      .logout-spinner span {
        width: 0.45rem;
        height: 0.45rem;
        border-radius: 999px;
        background: #0f766e;
        animation: logout-pulse 1s ease-in-out infinite;
      }

      .logout-spinner span:nth-child(2) {
        animation-delay: 0.15s;
      }

      .logout-spinner span:nth-child(3) {
        animation-delay: 0.3s;
      }

      @keyframes logout-pulse {
        0%,
        80%,
        100% {
          opacity: 0.25;
          transform: translateY(0);
        }
        40% {
          opacity: 1;
          transform: translateY(-3px);
        }
      }
    `,
  ],
})
export class AdminLogoutComponent implements OnInit, OnDestroy {
  private finished = false;
  private logoutSub?: Subscription;
  private fallbackTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private router: Router,
    private http: HttpClient,
    private api: ApiService,
    private common: CommonService,
    private notifications: NotificationsService,
  ) {}

  ngOnInit(): void {
    this.notifications.stop();
    this.clearClientSession();

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      this.finishLogout();
      return;
    }

    // Never leave the user stuck on this screen if the API is slow or unreachable.
    this.fallbackTimer = setTimeout(() => this.finishLogout(), 4000);

    this.logoutSub = this.http
      .post(
        `${this.api.getBaseUrl()}/logout`,
        {},
        {
          headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
        },
      )
      .pipe(timeout(3500))
      .subscribe({
        next: () => this.finishLogout(),
        error: () => this.finishLogout(),
      });
  }

  ngOnDestroy(): void {
    this.logoutSub?.unsubscribe();
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
    }
  }

  private finishLogout(): void {
    if (this.finished) {
      return;
    }
    this.finished = true;

    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = undefined;
    }

    localStorage.removeItem('jwtToken');
    this.clearClientSession();
    void this.router.navigateByUrl('/sso/admlogin', { replaceUrl: true });
  }

  private clearClientSession(): void {
    try {
      this.common.setMenu([]);
    } catch {
      // wire-frame may not always expose setMenu during teardown
    }

    const sessionKeys = [
      'loggedInUser',
      'clinic_id',
      'doctor_id',
      'clinic_name',
      'doctor_name',
      'context_override_active',
      'previous_clinic_id',
      'previous_doctor_id',
      'previous_clinic_name',
      'previous_doctor_name',
    ];
    sessionKeys.forEach((key) => sessionStorage.removeItem(key));
  }
}
